import { useEffect, useState } from "react";
import { useStore } from "../lib/store";
import SessionsTab from "./settings/SessionsTab";
import ChannelsTab from "./settings/ChannelsTab";
import RechtenTab from "./settings/RechtenTab";
import { isTauriSync } from "../lib/tauri";

type Tab = "rechten" | "sessions" | "channels";

/**
 * Overlay-modal voor instellingen + (admin-)beheer. Gebruikt hetzelfde
 * patroon als Lightbox: fixed inset-0, klik buiten = close, Escape =
 * close. Open-state komt uit zustand zodat StatusBar 'm via een knop
 * kan triggeren.
 *
 * Op een telefoon is het geen zwevend venster maar een vol scherm. Een dialoog
 * van 42rem die op 95vw wordt teruggeknepen houdt na de randen nog geen 300
 * pixels over, en dat is voor een lijst met apparaten al krap. Vanaf `sm`
 * (640px) is het weer het venster dat het altijd was.
 *
 * `fixed` betekent ook dat deze laag buiten #root valt, en daarmee buiten de
 * padding die de Android-app daar bovenop zet om onder de statusbalk uit te
 * blijven. Vandaar de klasse `settings-overlay`, waar styles.css de
 * safe-area-marges op zet - dezelfde truc als bij .mobile-sidebar.
 */
export default function SettingsModal() {
  const open = useStore((s) => s.settingsOpen);
  const close = useStore((s) => s.closeSettings);
  const me = useStore((s) => s.me);
  /* Rechten staat vooraan en is de eerste die je ziet: het is het enige
     tabblad met dingen die stuk kunnen staan zonder dat je het merkt. Op Windows
     regelt het besturingssysteem dit zelf - daar valt hier niets te regelen,
     dus dan is het er niet. */
  const toontRechten = !isTauriSync();
  const [tab, setTab] = useState<Tab>(toontRechten ? "rechten" : "sessions");
  const isAdmin = !!me?.is_admin;

  // Niet-admins kunnen het Kanalen-tab nooit zien; reset bij sluiten.
  useEffect(() => {
    if (!open) return;
    if (!isAdmin && tab === "channels") setTab("sessions");
    if (!toontRechten && tab === "rechten") setTab("sessions");
  }, [open, isAdmin, toontRechten, tab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      className="settings-overlay fixed inset-0 z-40 flex items-stretch justify-center bg-black/60 sm:items-center sm:p-6"
      onClick={close}
    >
      <div
        className="flex w-full flex-col overflow-hidden border-slate-700 bg-slate-800 shadow-2xl sm:h-[36rem] sm:max-h-[80vh] sm:w-[42rem] sm:max-w-[95vw] sm:rounded-lg sm:border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-700 px-4 py-2">
          <div className="flex gap-1">
            {toontRechten && (
              <TabButton active={tab === "rechten"} onClick={() => setTab("rechten")}>
                Rechten
              </TabButton>
            )}
            <TabButton active={tab === "sessions"} onClick={() => setTab("sessions")}>
              Sessies
            </TabButton>
            {isAdmin && (
              <TabButton active={tab === "channels"} onClick={() => setTab("channels")}>
                Kanalen
              </TabButton>
            )}
          </div>
          <button
            onClick={close}
            aria-label="Sluiten"
            className="flex h-10 w-10 items-center justify-center rounded text-lg text-slate-400 hover:bg-slate-700 hover:text-slate-200 sm:h-8 sm:w-8 sm:text-base"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {tab === "rechten" && toontRechten && <RechtenTab />}
          {tab === "sessions" && <SessionsTab />}
          {tab === "channels" && isAdmin && <ChannelsTab />}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded px-3 py-2 text-sm sm:py-1 ${
        active ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:bg-slate-700 hover:text-slate-200"
      }`}
    >
      {children}
    </button>
  );
}
